import React, { FC, ReactNode, useState } from 'react';

import { ClickAwayListener, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';

import FacebookIcon from '~/assets/icons/FacebookIcon';
import LinkedInIcon from '~/assets/icons/LinkedInIcon';
import MicrosoftTeamsIcon from '~/assets/icons/MicrosoftTeamsIcon';
import TwitterIcon from '~/assets/icons/TwitterIcon';
import { useTenantContext } from '~/context/TenantProvider';
import useShareTeams from '~/hooks/useShareTeams';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import { SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';
import { TENANTS_CONFIG } from '~/utils/tenantsConfig';

export enum Align {
  Left = 'left',
  Right = 'right',
}

interface ShareDropdownProps {
  align?: Align;
  children: ReactNode;
}

const ShareDropdown: FC<ShareDropdownProps> = ({ align = Align.Right, children, ...rest }) => {
  const theme = useTheme();
  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const { t } = useTenantTranslation();
  const { tenant } = useTenantContext();
  const { handleTeamsShare, teamsShareRef } = useShareTeams();

  const shareUrl = `${window?.location?.origin}${window?.location?.pathname}`;

  const toggleDropdown = () => {
    setDropdownOpen((prevState) => !prevState);
    if (!isDropdownOpen) {
      sendAnalyticEvent(VIDEO_PAGE_EVENTS.share);
    }
  };

  const closeDropdown = () => setDropdownOpen(false);
  const shareText = t(`page.video.${TENANTS_CONFIG[tenant?.name || '']?.videoShareTextKey}`, {
    title: tenant?.title,
  });

  const handleClickShareTeams = () => {
    sendAnalyticEvent(VIDEO_PAGE_EVENTS.shareMicrosoftTeams);
    handleTeamsShare();
  };

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <Wrapper {...rest}>
        <ChildrenWrapper data-testid="shareDropdownButton" onClick={toggleDropdown}>
          {children}
        </ChildrenWrapper>
        <Dropdown data-testid="shareDropdown" opened={isDropdownOpen} align={align}>
          <DropdownContent align={align}>
            <List>
              <ListItem>
                <LinkedinShareButton
                  data-testid="shareLinkedin"
                  title={shareText}
                  url={`${shareUrl}?utm_source=LinkedIn&utm_medium=organic_social&utm_campaign=video_share`}
                  resetButtonStyle={true}
                  onClick={() => sendAnalyticEvent(VIDEO_PAGE_EVENTS.shareLinkedIn)}
                >
                  <LinkedInIcon color={theme.palette.common.white} />
                  <ListItemText>{t('page.video.linkedIn')}</ListItemText>
                </LinkedinShareButton>
              </ListItem>
              <ListItem>
                <FacebookShareButton
                  data-testid="shareFacebook"
                  quote={shareText}
                  url={`${shareUrl}?utm_source=Facebook&utm_medium=organic_social&utm_campaign=video_share`}
                  resetButtonStyle={true}
                  onClick={() => sendAnalyticEvent(VIDEO_PAGE_EVENTS.shareFacebook)}
                >
                  <FacebookIcon color={theme.palette.common.white} />
                  <ListItemText>{t('page.video.facebook')}</ListItemText>
                </FacebookShareButton>
              </ListItem>
              <ListItem>
                <TwitterShareButton
                  data-testid="shareTwitter"
                  title={shareText}
                  url={`${shareUrl}?utm_source=Twitter&utm_medium=organic_social&utm_campaign=video_share`}
                  resetButtonStyle={true}
                  onClick={() => sendAnalyticEvent(VIDEO_PAGE_EVENTS.shareTwitter)}
                >
                  <TwitterIcon color={theme.palette.common.white} />
                  <ListItemText>{t('page.video.twitter')}</ListItemText>
                </TwitterShareButton>
              </ListItem>
              <ListItem>
                <TeamsShareButton onClick={handleClickShareTeams}>
                  <MicrosoftTeamsIcon color={theme.palette.common.white} />
                  <ListItemText>{t('page.test.microsoftTeams')}</ListItemText>
                </TeamsShareButton>
              </ListItem>
            </List>
          </DropdownContent>
        </Dropdown>
        <TeamsHiddenElement
          className="teams-share-button"
          data-href={`${shareUrl}?utm_source=Microsoft_teams&utm_medium=organic_social&utm_campaign=video_share`}
          ref={teamsShareRef}
          data-msg-text={shareText}
        />
      </Wrapper>
    </ClickAwayListener>
  );
};

export default ShareDropdown;

const Wrapper = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
`;
const Dropdown = styled('div', {
  shouldForwardProp: (prop) => prop !== 'opened' && prop !== 'align',
})<{ align: Align; opened: boolean }>`
  border-radius: 10px;
  background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  z-index: 200;
  box-shadow: 0 13px 26px ${({ theme }) => alpha(theme.palette.common.blackPure, 0.3)};
  min-width: 240px;
  max-width: 240px;
  position: absolute;
  left: ${({ align }) => (align === Align.Left ? '-15px' : 'unset')};
  right: ${({ align }) => (align === Align.Left ? 'unset' : '-15px')};
  top: 35px;
  transition: all 0.5s ease-in-out;
  display: ${({ opened }) => (opened ? 'block' : 'none')};
`;

const DropdownContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'align',
})<{ align: Align }>`
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
    transform: rotate(45deg);
    position: absolute;
    top: -25px;
    left: ${({ align }) => (align === Align.Left ? '20px' : 'unset')};
    right: ${({ align }) => (align === Align.Left ? 'unset' : '20px')};
  }
`;

const List = styled('ul')`
  padding-left: 0;
  margin: ${SMALL_MARGIN_PX} 0;
`;

const ListItem = styled('li')`
  display: flex;

  &:hover {
    background-color: ${({ theme }) => theme.palette.common.blackSecondary};
    cursor: pointer;
  }

  & > button {
    padding: ${TINY_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} !important;
    display: flex;
    align-items: center;
    width: 100%;
  }

  & svg {
    width: 18px;
    height: 18px;
    margin-right: ${TINY_MARGIN_PX};
  }
`;

const ListItemText = styled(Typography)`
  font-size: 14px;
  line-height: 1;
  font-weight: bold;
  color: ${({ theme }) => theme.palette.common.white};
  text-transform: uppercase;
`;
const TeamsHiddenElement = styled('div')`
  display: none;
`;
const TeamsShareButton = styled('button')`
  display: flex;
  padding: ${TINY_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  background: transparent;
  border: none;
  cursor: pointer;
`;
const ChildrenWrapper = styled('div')`
  display: flex;
`;
